import {
    AfterViewInit,
    Directive,
    DoCheck,
    ElementRef,
    HostListener,
    inject,
    Inject,
    Input,
    KeyValueChangeRecord,
    KeyValueDiffer,
    KeyValueDiffers,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    DOCUMENT
} from '@angular/core';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { ResizeService } from '@df/ui/common/resize.service';
import { PhotoswipeService } from '@df/ui/photoswipe/photoswipe.service';
import { PhotoswipeItem, PhotoswipeOptions } from '@df/ui/photoswipe/photoswipe.types';

let photoswipeUid = 0;

@Directive({
    selector: '[photoswipe]',
    standalone: true
})
export class PhotoswipeDirective implements OnChanges, AfterViewInit, OnDestroy, DoCheck, OnInit {
    /**
     * Source image
     */
    @Input()
    photoswipe?: string;

    @Input()
    photoswipeDisabled = false;

    @Input()
    photoswipeUid?: string;

    @Input()
    photoswipeOptions: Partial<PhotoswipeOptions> = {};

    @Input()
    photoswipeHtml?: string;

    @Input()
    photoswipeThumbnail?: string;

    @Input()
    photoswipeTitle?: string;

    private _inputObserver!: MutationObserver;
    private _item!: PhotoswipeItem;

    private _isExternal?: boolean;
    private _isDestroyed = false;

    private reinit: () => void;

    private _differsService = inject(KeyValueDiffers);
    private _differ!: KeyValueDiffer<string, PhotoswipeOptions>;

    constructor(
        protected elementRef: ElementRef,
        protected resizeService: ResizeService,
        protected renderer2: Renderer2,
        protected app: App,
        private timeoutService: TimeoutService,
        protected photoswipeService: PhotoswipeService,
        @Inject(DOCUMENT) protected document: Document,
        private afterContentInitDirectiveService: AfterContentInitDirectiveService
    ) {
        this.reinit = this.timeoutService.debounce(() => this._reinit(), 200);
        afterContentInitDirectiveService.lock(this);
    }

    get item(): PhotoswipeItem {
        return this._item;
    }

    set item(value: PhotoswipeItem) {
        this._item = {
            ...this._item,
            ...value
        };
    }

    private _reinit() {
        if (!this.photoswipe || this._isDestroyed) {
            return;
        }

        this._isExternal = !!this.photoswipe?.match(/^https?:\/\//);

        const w = this.elementRef.nativeElement.width;
        const h = this.elementRef.nativeElement.height;
        const scale = this.photoswipeOptions?.maxZoom || 1;

        const src = this.photoswipeHtml
            ? undefined
            : this._isExternal
              ? this.photoswipe
              : this.resizeService.resize(this.app.url.toMediaUrl(this.photoswipe), {
                    width: w * scale,
                    height: h * scale,
                    ...this.photoswipeOptions?.imageOptions
                });

        const msrc = this.photoswipeHtml
            ? undefined
            : this._isExternal
              ? this.photoswipe
              : this.resizeService.resize(this.app.url.toMediaUrl(this.photoswipe), {
                    width: w,
                    height: h,
                    ...this.photoswipeOptions?.imageOptions
                });
        if (!this.item || (this.item && msrc !== this.item.msrc)) {
            // register image
            this.item = this.photoswipeService.add(
                {
                    src,
                    msrc,
                    w: w * scale,
                    h: h * scale,
                    uid: this.uid,
                    index: this.index,
                    source: this.photoswipeThumbnail || this.photoswipe,
                    html: this.photoswipeHtml,
                    fetch: !!this.photoswipeOptions.maxZoom,
                    onFocusOnly: this.photoswipeOptions.onFocusOnly,
                    title: this.photoswipeTitle
                },
                this.galleryUID,
                this.index
            );

            this.fetch();
        }
    }

    ngOnInit(): void {
        this._differ = this._differsService.find(this.photoswipeOptions).create();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['photoswipeOptions']) {
            this.photoswipeService.updateOptions(changes['photoswipeOptions'].currentValue);
        }
        this._inputObserver?.disconnect();
    }

    ngDoCheck(): void {
        if (this._differ) {
            const changes = this._differ.diff(this.photoswipeOptions as unknown as Map<string, PhotoswipeOptions>);

            if (changes) {
                changes.forEachChangedItem((record: KeyValueChangeRecord<string, PhotoswipeOptions>) => {
                    if (record.key === 'galleryUID') {
                        this.photoswipeService.updateOptions(this.photoswipeOptions);
                    }
                });
            }
        }
    }

    ngAfterViewInit(): void {
        if (this.app.isPlatformBrowser) {
            this._inputObserver = new MutationObserver(() => {
                this.timeoutService.async(() => {
                    this.reinit();
                });
            });

            this._inputObserver.observe(this.elementRef.nativeElement, {
                attributes: true
            });
            this.reinit();
        }

        this.afterContentInitDirectiveService.unlock(this);
    }

    ngOnDestroy(): void {
        this.afterContentInitDirectiveService.unlock(this);
        this._isDestroyed = true;
        this.loading();
    }

    get galleryUID(): number {
        return this.photoswipeOptions?.galleryUID || this.photoswipeService.options?.galleryUID || 1;
    }

    get index(): number {
        return this.photoswipeOptions?.index || 0;
    }

    get uid(): string {
        return this.photoswipeUid || this.photoswipe || `photoswipe.${photoswipeUid++}`;
    }

    @HostListener('mouseenter', ['$event.target'])
    fetch(force = false): Promise<void | PhotoswipeItem[]> {
        return new Promise((resolve, reject) => {
            this.item = this.photoswipeService.exists(this.item, this.galleryUID)!;

            if (typeof force !== 'boolean') {
                force = false;
            }

            if ((!force && !this.photoswipeOptions.canPreload) || this.photoswipeOptions.maxZoom || this.item.fetch) {
                return resolve();
            }

            // original size image
            if (this.photoswipe) {
                const src = this._isExternal
                    ? this.photoswipe
                    : this.resizeService.resize(this.app.url.toMediaUrl(this.photoswipe), {
                          width: this.photoswipeOptions?.imageOptions?.width
                              ? this.photoswipeOptions?.imageOptions?.width
                              : this.photoswipeOptions?.originalZoom
                                ? this.item.w! * this.photoswipeOptions.originalZoom
                                : 0,
                          height: this.photoswipeOptions?.imageOptions?.height
                              ? this.photoswipeOptions?.imageOptions?.height
                              : this.photoswipeOptions?.originalZoom
                                ? this.item.h! * this.photoswipeOptions.originalZoom
                                : 0,
                          ...this.photoswipeOptions?.imageOptions
                      });
                this.item.src = src;
            }

            this.photoswipeService
                .fetchAndUpdate(this.item, this.galleryUID, force, this._isExternal)
                .then(items => {
                    this.item = this.photoswipeService.exists(this.item, this.galleryUID)!;

                    resolve(items);
                })
                .catch(() => reject());
        });
    }

    @HostListener('click')
    onClick(): void {
        if (this.photoswipeDisabled) {
            return;
        }

        this.loading(true);

        const index = this.photoswipeService.index(this.item, this.galleryUID)!;

        if (this.photoswipeOptions?.loadAfterOpen) {
            this.photoswipeService
                .open(
                    {
                        galleryUID: this.galleryUID,
                        index,
                        getThumbBoundsFn: () => {
                            const thumbnail = this.elementRef.nativeElement; // find thumbnail
                            const pageYScroll = window.pageYOffset || this.document.documentElement.scrollTop;
                            const rect = thumbnail.getBoundingClientRect();

                            return {
                                x: rect.left,
                                y: rect.top + pageYScroll,
                                w: rect.width
                            };
                        }
                    },
                    this.item
                )
                .then(() => {
                    this.loading();
                    this.photoswipeService.fetchAll(this.galleryUID).then(() => {
                        this.photoswipeService.updateSize();
                    });
                })
                .catch(() => this.loading());
        } else {
            this.photoswipeService.fetchAll(this.galleryUID).then(() => {
                this.photoswipeService
                    .open(
                        {
                            galleryUID: this.galleryUID,
                            index,
                            getThumbBoundsFn: () => {
                                const thumbnail = this.elementRef.nativeElement; // find thumbnail
                                const pageYScroll = window.pageYOffset || this.document.documentElement.scrollTop;
                                const rect = thumbnail.getBoundingClientRect();

                                return {
                                    x: rect.left,
                                    y: rect.top + pageYScroll,
                                    w: rect.width
                                };
                            }
                        },
                        this.item
                    )
                    .then(() => {
                        this.loading();
                    })
                    .catch(() => this.loading());
            });
        }
    }

    protected loading(busy = false): void {
        if (this.app.isPlatformBrowser) {
            busy
                ? this.renderer2.addClass(this.document.body, 'photoswipe-loading')
                : this.renderer2.removeClass(this.document.body, 'photoswipe-loading');

            const galleries = this.document.querySelectorAll(`.photoswipe-galleryuid-${this.galleryUID}`);

            if (galleries && galleries.length) {
                galleries.forEach(gallery => {
                    busy
                        ? this.renderer2.addClass(gallery, 'photoswipe-loading')
                        : this.renderer2.removeClass(gallery, 'photoswipe-loading');
                });
            }

            const index = `${this.index}`.replace(/\./g, '-');

            const suffix = typeof this.index === 'number' ? `${this.galleryUID}-${index}` : index;
            const items = this.document.querySelectorAll(`.photoswipe-index-${suffix}`);

            if (items && items.length) {
                items.forEach(item => {
                    busy
                        ? this.renderer2.addClass(item, 'photoswipe-loading-index')
                        : this.renderer2.removeClass(item, 'photoswipe-loading-index');
                });
            }
        }
    }
}
